import { useRef, useState } from "react";
import CodePanel from "../codePanel";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import {
  MOTION_OK,
  prefersReducedMotion,
  revealTrigger,
} from "../../utils/animations";

export default function Markets() {
  const sectionRef = useRef(null);
  const indicatorRef = useRef(null);
  const btnRefs = useRef({});
  const firstRun = useRef(true);
  const [activeProduct, setActiveProduct] = useState("digital-analytics");
  const [activeTab, setActiveTab] = useState("javascript");

  const tabs = [
    { id: "digital-analytics", label: "Digital Analytics" },
    { id: "capital-markets", label: "Capital Markets" },
  ];

  // Keep the active id available to listeners without stale closures.
  const activeProductRef = useRef(activeProduct);
  activeProductRef.current = activeProduct;

  // Slide the background pill to sit behind the active tab. Skips the
  // tween (duration 0) on first paint and for reduced-motion users.
  const positionIndicator = (animate) => {
    const btn = btnRefs.current[activeProductRef.current];
    const ind = indicatorRef.current;
    if (!btn || !ind) return;
    gsap.to(ind, {
      x: btn.offsetLeft,
      y: btn.offsetTop,
      width: btn.offsetWidth,
      height: btn.offsetHeight,
      duration: animate && !prefersReducedMotion() ? 0.45 : 0,
      ease: "power3.out",
    });
  };

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        gsap
          .timeline({
            defaults: { ease: "power3.out" },
            scrollTrigger: revealTrigger(sectionRef.current, "top 70%"),
          })
          .from(".markets-tabs", { y: 24, autoAlpha: 0, duration: 0.7 })
          .from(
            ".markets-copy > *",
            { y: 28, autoAlpha: 0, duration: 0.8, stagger: 0.15 },
            "-=0.3"
          )
          .from(
            ".markets-code",
            { x: 48, autoAlpha: 0, duration: 1 },
            "-=0.5"
          );
      });
      return () => mm.revert();
    },
    { scope: sectionRef }
  );

  // Move the indicator and cross-fade the panel content whenever the
  // active product changes. Reposition on resize so it stays aligned.
  useGSAP(
    () => {
      positionIndicator(!firstRun.current);

      if (!firstRun.current && !prefersReducedMotion()) {
        gsap.fromTo(
          ".markets-copy > *",
          { autoAlpha: 0, y: 16 },
          { autoAlpha: 1, y: 0, duration: 0.5, stagger: 0.08, ease: "power3.out" }
        );
        gsap.fromTo(
          ".markets-code",
          { autoAlpha: 0, y: 16 },
          { autoAlpha: 1, y: 0, duration: 0.55, ease: "power3.out" }
        );
      }
      firstRun.current = false;

      const onResize = () => positionIndicator(false);
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    },
    { scope: sectionRef, dependencies: [activeProduct] }
  );

  return (
    <div
      ref={sectionRef}
      className="min-h-screen relative bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 container py-[96px]"
    >
      {/* Background decoration */}
      <div className="overflow-hidden pointer-events-none">
        <div className="absolute top-0 md:top-20 left-[50%] -translate-x-[50%] w-full md:w-[900px] h-[180px] bg-[#0073FF] rounded-full blur-[200px]"></div>
      </div>

      <div className="absolute bottom-44 md:bottom-36 left-[50%] -translate-x-[50%] w-full md:w-[900px] h-[180px] bg-[#0073FF] rounded-full blur-[200px]"></div>
      {/* Main content */}

      {/* Main content area */}
      <div className="p-0 md:p-12 relative z-10 md:bg-[rgba(255,255,255,0.05)] space-y-[28px] rounded-xl">
        <div className="markets-tabs bg-[#EFF0F726] rounded-tr-2xl rounded-tl-2xl w-full lg:w-[40%]">
          <div className="flex items-end bg-[#101A52]/40 md:pt-3 rounded-tr-2xl rounded-tl-2xl md:pl-4 relative">
            {/* Sliding background pill — GSAP moves it behind the active tab */}
            <div
              ref={indicatorRef}
              aria-hidden="true"
              className="absolute left-0 top-0 z-0 bg-[#EFF0F726] rounded-t-2xl shadow-[0_4px_15px_rgba(0,0,0,0.2)] pointer-events-none will-change-transform"
            ></div>
            {tabs.map((tab) => {
              const isActive = activeProduct === tab.id;
              return (
                <button
                  key={tab.id}
                  ref={(el) => (btnRefs.current[tab.id] = el)}
                  onClick={() => setActiveProduct(tab.id)}
                  className={`relative z-10 px-5 py-3 md:py-2 text-xs md:text-base font-extrabold rounded-t-2xl rounded-b-none transition-colors duration-300 w-full md:w-auto
                ${isActive ? "text-white" : "text-[#C0C0C0] hover:text-white"}`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <div className="w-full bg-[#EFF0F726] h-[6px] md:h-[8px]"></div>
        </div>
        <div className="flex flex-col lg:flex-row gap-[60px] xl:gap-[30px] items-start">
          {/* Left column */}
          <div className="markets-copy basis-2/4 flex-shrink-0">
            {/* Main headline */}
            <div className="space-y-2 md:space-y-1">
              <h1 className="text-base md:text-2xl font-extrabold text-[#88BEFF] leading-tight">
                {activeProduct === "digital-analytics"
                  ? "One SDK for Web, Mobile, and Chat Interfaces"
                  : "Built for Developers. Tuned for Agents."}
              </h1>
            </div>

            {/* Description */}
            <p className="text-sm md:text-base text-[#C0C0C0] leading-relaxed mt-3">
              {activeProduct === "digital-analytics"
                ? "Data Assistant watches your data continuously, reasons through what's changing and why, and acts on your behalf, within boundaries you define. Powered by Pulse for collection, CORE for reasoning, and [AI] Platform for scale."
                : "Deliver real-time and historical capital market data to LLMs and autonomous agents through a unified API and low-latency streams. Normalised schemas and consistent structures improve tool-calling reliability and reasoning accuracy. Our MCP layer also integrates contextual signals such as news, reports, and domain metadata to support enhanced AI reasoning."}
            </p>
          </div>

          {/* Right column - Code panel */}
          <div className="markets-code sticky top-8 w-full md:basis-2/4 grow-0">
            <CodePanel
              activeTab={activeTab}
              onTabChange={setActiveTab}
              product={activeProduct}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
